import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase URL or Anon Key is missing. Check your .env file.');
}

// Track whether we've already handled a fatal auth error to prevent loops
let fatalAuthHandled = false;

export function resetFatalAuthFlag() {
  fatalAuthHandled = false;
}

// Don't auto-detect auth tokens on the Google callback page — the Google OAuth
// hash (#access_token=...) would be mistaken for a Supabase auth token and
// can corrupt / clear the active session.
const isGoogleCallbackPage =
  typeof window !== 'undefined' &&
  window.location.pathname.includes('google-callback');

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: !isGoogleCallbackPage,
  },
  global: {
    fetch: async (input, init) => {
      const response = await fetch(input, init);
      
      // Clone the response so we can read the body without consuming it
      const clone = response.clone();
      
      try {
        const body = await clone.text();
        // Detect "Invalid Refresh Token" errors from Supabase Auth
        if (
          response.status === 400 &&
          (body.includes('Invalid Refresh Token') || body.includes('Refresh Token Not Found'))
        ) {
          if (!fatalAuthHandled) {
            fatalAuthHandled = true;
            console.warn('[Supabase] Refresh token inválido detectado — limpando sessão.');
            // Force sign out to clear the broken session
            // Clear only the local session — do NOT call the server logout
            // endpoint, because the refresh token is already invalid and the
            // server call would fail again (and log another error).
            supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          }
        }
      } catch {
        // If we can't read the body, just continue normally
      }
      
      return response;
    },
  },
});

// Reset the fatal flag whenever a fresh session is established so future
// session expirations are also handled gracefully (covers flows that don't
// pass through the /login page, like OAuth callbacks).
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_IN') {
    fatalAuthHandled = false;
  }
});

export default supabase;